import React, { useRef, useEffect, useState } from "react";
import { Box, Grow, ClickAwayListener } from "@material-ui/core";
import ReactCountryFlag from "react-country-flag";
import { IoMdArrowDropdown } from "react-icons/io";
import _ from "lodash";
import voca from "voca";
import { FormattedMessage, useIntl } from "react-intl";
import parsePhoneNumber from "libphonenumber-js";
import { CountryCode, PhoneNumber } from "libphonenumber-js/types";

import { useStyles } from "./PhoneNumberInput.style";
import Country from "../../models/Country";
import CountryService from "../../services/CountryService";
import { PHONE_NUMBER_MAX_SIZE } from "../../settings/constants";

export interface PhoneNumberInputProps {
  defaultCountry?: Country;
  defaultPhone?: string;

  name: string;
  id?: string;
  label?: string;
  error?: React.ReactNode;
  size?: "large" | "medium" | "small";
  disabled?: boolean;
  onChange?: (phoneNumber: PhoneNumber | null) => void;
}

function initCountryState(
  intl: any,
  defaultCountry: Country | null | undefined
) {
  return defaultCountry !== null && defaultCountry !== undefined
    ? defaultCountry
    : Country.defaultCountry(intl.formatMessage({ id: "country.cameroon" }));
}

export const PhoneNumberInput = (props: PhoneNumberInputProps) => {
  const wrapperRef = useRef(null);
  const classes = useStyles(props);
  const intl = useIntl();
  const inputId = props.id ? props.id : _.uniqueId("phone-input");
  // Props
  const {
    onChange,
    label,
    error,
    disabled,
    defaultCountry,
    defaultPhone,
    name,
  } = props;
  // State
  const [open, setOpen] = React.useState(false);
  const [country, setCountry] = useState(
    (): Country => initCountryState(intl, defaultCountry)
  );
  const [phone, setPhone] = useState(
    defaultPhone !== null && defaultPhone !== undefined ? defaultPhone : ""
  );
  const [countries, setCountries] = useState((): Array<Country> => []);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [localError, setLocalError] = useState((): string => null);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const loadCountries = () => {
    setLoading(true);
    const response = CountryService.supportedCountries();

    response.then((response) => {
      let results: Array<Country> = [];
      setLoading(false);

      if (response.isSuccess && response.data.code === 100) {
        JSON.parse(JSON.stringify(response.data.data)).forEach(
          (item, index) => {
            results.push(Country.fromJson(item));
          }
        );
        setCountries(results);
      } else {
        setCountries([]);
      }
    });
  };

  const handleInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    let currentPhone: string = event.currentTarget.value;

    const phoneNumberProto = parsePhoneNumber(
      currentPhone,
      country.iso_code.toUpperCase() as CountryCode
    );

    if (currentPhone.length !== 0) {
      if (phoneNumberProto) {
        if (
          phoneNumberProto.nationalNumber.toString().length >=
            PHONE_NUMBER_MAX_SIZE + 1 ||
          !Number(phoneNumberProto.nationalNumber.toString())
        ) {
          return;
        }

        handleInputState(phoneNumberProto, currentPhone);
      } else {
        if (!Number(currentPhone)) return;
        handleInputState(null, currentPhone);
      }
    } else {
      handleInputState(null, "");
    }
  };

  const handleSelect = (item: Country) => {
    setOpen((prev) => !prev);
    setCountry(item);

    handleInputState(null, "");
    if (onChange !== null && onChange !== undefined) {
      props.onChange(null);
    }
  };

  const handleInputState = (
    phoneNumber: PhoneNumber | null,
    defaultPhone: string
  ): void => {
    setPhone(
      phoneNumber ? phoneNumber.formatNational().toString() : defaultPhone
    );

    if (onChange !== null && onChange !== undefined) {
      props.onChange(phoneNumber);
    }
  };

  const renderCountryItem = (item: Country, index: number) => {
    let className = `countryItem ${
      country.iso_code === item.iso_code ? "selected" : ""
    }`;

    return (
      <Box
        component="div"
        display="flex"
        alignItems="center"
        flexWrap="nowrap"
        className={className}
        onClick={() => handleSelect(item)}
        key={index}
      >
        <ReactCountryFlag
          className="flag"
          countryCode={item.iso_code.toUpperCase()}
          svg
          style={{
            fontSize: "16px",
            marginRight: "14px",
            marginTop: "1px",
            lineHeight: "2em",
          }}
          aria-label={item.designation}
        />
        <span className="country">{voca.titleCase(item.designation)}</span>
        <span className="code">{item.dial_code}</span>
      </Box>
    );
  };

  const buildResults = () => {
    if (loading) {
      return (
        <span className="placeholder">
          <FormattedMessage id="sharedScreen.loading" /> ...
        </span>
      );
    } else {
      if (countries.length === 0) {
        return (
          <div className="placeholder">
            <Box
              display="flex"
              flexWrap="nowrap"
              alignItems="center"
              justifyContent="space-between"
            >
              <div>
                <FormattedMessage id="sharedScreen.noCountry" />
              </div>
              <span
                className="placeholderExtra"
                onClick={(_) => loadCountries()}
              >
                <FormattedMessage id="sharedScreen.retry" />
              </span>
            </Box>
          </div>
        );
      } else {
        return (
          <>
            <span className="placeholder">Select country</span>
            <div className="countryItems">
              {countries.map((item, index) => {
                return renderCountryItem(item, index);
              })}
            </div>
          </>
        );
      }
    }
  };

  useEffect(() => {
    loadCountries();
  }, []);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div className={classes.root} ref={wrapperRef}>
        {label ? <label htmlFor={inputId}>{label}</label> : <></>}
        <Box className={classes.innerRoot}>
          <Box component="div" className={classes.wrapper}>
            <Box
              component="div"
              className={classes.wrapperInner}
              display="flex"
              alignItems="center"
              flexWrap="nowrap"
            >
              <div className={classes.isoWrapper} onClick={handleOpen}>
                <Box
                  component="div"
                  display="flex"
                  alignItems="center"
                  flexWrap="nowrap"
                >
                  <ReactCountryFlag
                    className="countryFlag"
                    countryCode={country.iso_code.toUpperCase()}
                    svg
                    style={{
                      fontSize: "20px",
                      lineHeight: "2em",
                    }}
                    aria-label="Cameroon"
                  />
                  <span className="arrow">
                    <IoMdArrowDropdown size={12} />
                  </span>
                  <Box
                    component="div"
                    display="flex"
                    justifyContent="flex-end"
                    flexWrap="nowrap"
                    className="dialCode"
                  >
                    {country.dial_code}
                  </Box>
                </Box>
              </div>
              <div className={classes.inputWrapper}>
                <input
                  id={inputId}
                  name={name}
                  type="text"
                  value={phone}
                  autoComplete="off"
                  onChange={handleInputChange}
                  disabled={disabled}
                />
              </div>
            </Box>
            {disabled ? <div className="overlay" /> : <></>}
          </Box>
          {localError ? (
            <Box className={classes.error}>{localError}</Box>
          ) : null}
          <Box className={classes.error}>{error}</Box>
          <Grow in={open} disableStrictModeCompat={false}>
            <Box className={classes.options} zIndex="modal">
              <div className={`optionWrapper ${open ? "open" : "close"}`}>
                {buildResults()}
              </div>
            </Box>
          </Grow>
        </Box>
      </div>
    </ClickAwayListener>
  );
};

PhoneNumberInput.defaultProps = {
  size: "medium",
  disable: false,
};
